import React, { useState } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button, Table } from "antd";

const { Title, Paragraph, Text } = Typography;

const LearningTechniques = () => {
  const [trained, setTrained] = useState(false);
  const [testItem, setTestItem] = useState(null);
  const [clustered, setClustered] = useState(false);

  const examples = [
    { features: "Red, round, sweet", label: "Apple", emoji: "🍎" },
    { features: "Yellow, curved, soft", label: "Banana", emoji: "🍌" },
    { features: "Orange, round, citrus", label: "Orange", emoji: "🍊" },
    { features: "Purple, small, bunch", label: "Grapes", emoji: "🍇" },
  ];

  const testItems = [
    { features: "Red, round", answer: "Apple", emoji: "🍎" },
    { features: "Yellow, curved", answer: "Banana", emoji: "🍌" },
    { features: "Orange, citrus", answer: "Orange", emoji: "🍊" },
  ];

  const customers = [
    { age: 16, spending: 25, group: "teens", emoji: "🧒" },
    { age: 18, spending: 30, group: "teens", emoji: "🧒" },
    { age: 35, spending: 180, group: "adults", emoji: "👨" },
    { age: 42, spending: 220, group: "adults", emoji: "👨" },
    { age: 65, spending: 90, group: "seniors", emoji: "👴" },
    { age: 70, spending: 85, group: "seniors", emoji: "👴" },
  ];

  const groups = {
    teens: {
      name: "Teen Shoppers",
      color: "#3b82f6",
      desc: "Young, low spending",
    },
    adults: {
      name: "Adult Professionals",
      color: "#10b981",
      desc: "Mid-age, high spending",
    },
    seniors: {
      name: "Senior Citizens",
      color: "#f59e0b",
      desc: "Elderly, moderate spending",
    },
  };

  const test = () => {
    const random = testItems[Math.floor(Math.random() * testItems.length)];
    setTestItem(random);
  };

  const renderOverview = () => {
    const learningData = [
      {
        method: "Supervised Learning",
        description:
          "Learning with a teacher - AI learns from labeled examples",
        analogy: "Like studying flashcards with questions AND answers",
        examples: "Email spam detection, image recognition, price prediction",
        dataType: "Labeled (input + answer)",
        goal: "Learn to predict",
        bestFor: "Classification, prediction",
      },
      {
        method: "Unsupervised Learning",
        description:
          "Finding patterns without labels - AI discovers hidden structures",
        analogy: "Like organizing your closet without being told how",
        examples:
          "Customer segmentation, recommendation systems, anomaly detection",
        dataType: "Unlabeled (no answers)",
        goal: "Find patterns",
        bestFor: "Clustering, grouping",
      },
      {
        method: "Reinforcement Learning",
        description: "Learning through trial and error with rewards",
        analogy: "Like learning to play games - try, fail, adjust, improve",
        examples:
          "Game AI (AlphaGo), robotics, self-driving cars, ChatGPT training",
        dataType: "Reward/penalty signals",
        goal: "Maximize rewards",
        bestFor: "Games, robotics, decisions",
      },
    ];

    return (
      <>
        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              Machine Learning Types Comparison
            </span>
          }
        >
          {/* Desktop Table View */}
          <div className="desktop-table">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        borderBottom: "2px solid #0071e3",
                        width: "20%",
                      }}
                    >
                      <Text strong>Method</Text>
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        borderBottom: "2px solid #0071e3",
                        width: "25%",
                      }}
                    >
                      <Text strong>Description & Examples</Text>
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        borderBottom: "2px solid #0071e3",
                        width: "20%",
                      }}
                    >
                      <Text strong>Analogy</Text>
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        borderBottom: "2px solid #0071e3",
                        width: "15%",
                      }}
                    >
                      <Text strong>Data Type</Text>
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        borderBottom: "2px solid #0071e3",
                        width: "15%",
                      }}
                    >
                      <Text strong>Learning Goal</Text>
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        borderBottom: "2px solid #0071e3",
                        width: "15%",
                      }}
                    >
                      <Text strong>Best For</Text>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {learningData.map((row, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom:
                          idx < learningData.length - 1
                            ? "2px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <td
                        style={{
                          padding: "16px",
                          verticalAlign: "top",
                          textAlign: "left",
                        }}
                      >
                        <Text strong style={{ color: "#0071e3" }}>
                          {row.method}
                        </Text>
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          verticalAlign: "top",
                        }}
                      >
                        <div style={{ marginBottom: "8px" }}>
                          {row.description}
                        </div>
                        <div>
                          <Text strong>Examples: </Text>
                          {row.examples}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          verticalAlign: "top",
                        }}
                      >
                        {row.analogy}
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          verticalAlign: "top",
                        }}
                      >
                        {row.dataType}
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          verticalAlign: "top",
                        }}
                      >
                        {row.goal}
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          verticalAlign: "top",
                        }}
                      >
                        {row.bestFor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {learningData.map((item, idx) => (
                <Card
                  key={idx}
                  size="small"
                  title={
                    <Text strong style={{ color: "#0071e3", fontSize: "16px" }}>
                      {item.method}
                    </Text>
                  }
                  style={{ border: "1px solid #0071e3", borderRadius: "8px" }}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <div>
                      <Text strong>Description: </Text>
                      {item.description}
                    </div>
                    <div>
                      <Text strong>Examples: </Text>
                      {item.examples}
                    </div>
                    <div>
                      <Text strong>Analogy: </Text>
                      {item.analogy}
                    </div>
                    <div>
                      <Text strong>Data Type: </Text>
                      {item.dataType}
                    </div>
                    <div>
                      <Text strong>Learning Goal: </Text>
                      {item.goal}
                    </div>
                    <div>
                      <Text strong>Best For: </Text>
                      {item.bestFor}
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        </Card>

        <style>{`
        @media (min-width: 769px) {
          .desktop-table {
            display: block;
          }
          .mobile-cards {
            display: none;
          }
        }
        @media (max-width: 768px) {
          .desktop-table {
            display: none;
          }
          .mobile-cards {
            display: block;
          }
        }
      `}</style>
      </>
    );
  };

  const renderSupervised = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>What is Supervised Learning?</span>
        }
      >
        <Paragraph>
          AI learns from <Text strong>labeled examples</Text> - like a teacher
          giving questions with answer keys. After seeing many examples, models
          learns to predict answers for new, unseen data.
        </Paragraph>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Training Data (Examples with Labels)
          </span>
        }
      >
        <Row gutter={[16, 16]}>
          {examples.map((ex, idx) => (
            <Col xs={12} md={6} key={idx}>
              <Card
                size="small"
                style={{ textAlign: "center", borderColor: "#0071e3" }}
              >
                <div style={{ fontSize: "36px", marginBottom: "8px" }}>
                  {ex.emoji}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    marginBottom: "8px",
                    textAlign: "left",
                  }}
                >
                  <Text strong>Input: </Text>
                  {ex.features}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    marginBottom: "8px",
                    textAlign: "left",
                  }}
                >
                  <Text strong>Label: </Text>
                  {ex.label}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <div
          style={{
            textAlign: "center",
            marginTop: "24px",
            marginBottom: "24px",
          }}
        >
          <Space>
            <Button
              type="primary"
              onClick={() => setTrained(true)}
              disabled={trained}
            >
              {trained ? "✓ Model Trained" : "Train Model"}
            </Button>
            {trained && <Button onClick={test}>Test Model</Button>}
          </Space>
        </div>

        {testItem && (
          <Card
            style={{
              // backgroundColor: "#f6ffed",
              borderColor: "#52c41a",
              borderWidth: "1px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "48px", marginBottom: "8px" }}>
                {testItem.emoji}
              </div>
              <Paragraph>
                <Text strong>Features:</Text> {testItem.features}
              </Paragraph>
              <div
                style={{
                  padding: "12px",
                  background: "#d9f7be",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: 700,
                  // color: "#52c41a",
                }}
              >
                AI Predicts: {testItem.answer} ✓
              </div>
            </div>
          </Card>
        )}
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>How It Works</span>}>
        <ol style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>Training:</Text> Show AI many examples with correct
            labels
          </li>
          <li>
            <Text strong>Learning:</Text> AI finds patterns in the features
          </li>
          <li>
            <Text strong>Testing:</Text> Give AI new data without labels
          </li>
          <li>
            <Text strong>Prediction:</Text> AI uses learned patterns to guess
            the answer
          </li>
        </ol>
      </Card>
    </Space>
  );

  const renderUnsupervised = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            What is Unsupervised Learning?
          </span>
        }
      >
        <Paragraph>
          AI finds <Text strong>hidden patterns</Text> in data WITHOUT being
          told what to look for. It discovers groups and structures on its own -
          like organizing things without instructions.
        </Paragraph>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>Customer Data (No Labels!)</span>
        }
      >
        <Row gutter={[16, 16]}>
          {customers.map((c, idx) => (
            <Col xs={12} md={4} key={idx}>
              <Card
                size="small"
                style={{
                  textAlign: "center",
                  backgroundColor: clustered
                    ? `${groups[c.group].color}20`
                    : "white",
                  borderColor: clustered ? groups[c.group].color : "#d9d9d9",
                  borderWidth: "2px",
                  transition: "all 0.5s ease",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                  {c.emoji}
                </div>
                <div style={{ fontSize: "12px", textAlign: "left" }}>
                  Age: {c.age}
                  <br />
                  Amount: ${c.spending}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <div
          style={{
            textAlign: "center",
            marginBottom: "24px",
            marginTop: "24px",
          }}
        >
          <Button
            type="primary"
            onClick={() => setClustered(true)}
            disabled={clustered}
          >
            {clustered ? "✓ Clusters Found" : "Find Patterns"}
          </Button>
        </div>

        {clustered && (
          <Card
            title={<span style={{ color: "#0071e3" }}>Discovered Groups</span>}
            style={{ borderColor: "#52c41a" }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {Object.entries(groups).map(([key, g]) => (
                <div
                  key={key}
                  style={{
                    padding: "12px",
                    background: `${g.color}10`,
                    border: `1px solid ${g.color}`,
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong style={{ color: g.color }}>
                      {g.name}
                    </Text>
                    <div style={{ fontSize: "13px", color: "#666" }}>
                      {g.desc}
                    </div>
                  </div>
                  <Text strong style={{ color: g.color }}>
                    {customers.filter((c) => c.group === key).length} customers
                  </Text>
                </div>
              ))}
            </Space>
          </Card>
        )}
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>How It Works</span>}>
        <ol style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>No Labels:</Text> AI gets raw data without any
            categories
          </li>
          <li>
            <Text strong>Find Similarities:</Text> Measures which data points
            are similar
          </li>
          <li>
            <Text strong>Auto-Group:</Text> Creates clusters of similar items
          </li>
          <li>
            <Text strong>Discover Insights:</Text> Reveals hidden patterns in
            data
          </li>
        </ol>
      </Card>
    </Space>
  );

  const renderReinforcement = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            What is Reinforcement Learning?
          </span>
        }
      >
        <Paragraph>
          AI learns through <Text strong>trial and error</Text> by receiving
          rewards for good actions and penalties for bad ones. Over time, it
          discovers the best strategy to maximize rewards - just like learning
          to play games!
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>The Learning Loop</span>}>
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "40px",
                  margin: "0 auto 8px",
                }}
              >
                🤖
              </div>
              <Text strong>Agent</Text>
              <div style={{ fontSize: "11px", color: "#666" }}>
                Takes action
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "40px",
                  margin: "0 auto 8px",
                }}
              >
                🌍
              </div>
              <Text strong>Environment</Text>
              <div style={{ fontSize: "11px", color: "#666" }}>
                Gives reward
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "40px",
                  margin: "0 auto 8px",
                }}
              >
                🧠
              </div>
              <Text strong>Learn</Text>
              <div style={{ fontSize: "11px", color: "#666" }}>
                Improve strategy
              </div>
            </div>
          </Col>
          {/* <Col xs={12} md={6}>
            <div
              style={{
                textAlign: "center",
                fontSize: "48px",
                color: "#faad14",
                lineHeight: "100px",
              }}
            >
              ↻
            </div>
          </Col> */}

          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "40px",
                  margin: "0 auto 8px",
                  color: "#b1790aff",
                }}
              >
                ↻
              </div>
              <Text strong>Loop</Text>
              <div style={{ fontSize: "11px", color: "#666" }}>
                Repeat many times
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Famous Examples</span>}>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div>
            <Text strong>AlphaGo:</Text> Defeated world Go champion by learning
            from millions of self-played games
          </div>
          <div>
            <Text strong>Self-Driving Cars:</Text> Learn safe driving through
            simulation rewards/penalties
          </div>
          <div>
            <Text strong>ChatGPT (RLHF):</Text> Learns to be helpful from human
            feedback ratings
          </div>
        </Space>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Key Concepts</span>}>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            <Text strong>Agent:</Text> The AI making decisions
          </li>
          <li>
            <Text strong>Environment:</Text> The world it interacts with
          </li>
          <li>
            <Text strong>Actions:</Text> Moves the agent can make
          </li>
          <li>
            <Text strong>Rewards:</Text> Points for good actions, penalties for
            bad
          </li>
          <li>
            <Text strong>Policy:</Text> Strategy the agent learns over time
          </li>
        </ul>
      </Card>
    </Space>
  );

  const renderHistory = () => {
    const timeline = [
      {
        year: "1950s-1960s",
        title: "The Perceptron: First Learning Algorithm",
        event:
          "Frank Rosenblatt created the Perceptron, the first algorithm that could learn from examples (supervised learning). It could recognize simple patterns like letters. This was the birth of machine learning!",
      },
      {
        year: "1980s",
        title: "Backpropagation & Neural Networks",
        event:
          "Backpropagation algorithm was rediscovered, enabling multi-layer neural networks to learn complex patterns. This made supervised learning practical for real problems like handwriting recognition.",
      },
      {
        year: "1990s",
        title: "Support Vector Machines (SVMs)",
        event:
          "SVMs became popular for supervised learning tasks. They could classify data by finding optimal boundaries. Used widely for text classification, face detection, and more.",
      },
      {
        year: "2000s",
        title: "K-Means & Clustering Algorithms",
        event:
          "Unsupervised learning methods like K-Means became mainstream. Companies started using them to segment customers, find patterns in data, and make recommendations without labels.",
      },
      {
        year: "2013",
        title: "Deep Q-Learning (DQN)",
        event:
          "DeepMind combined deep learning with reinforcement learning to create DQN, which learned to play Atari games from pixels alone. This started the deep RL revolution.",
      },
      {
        year: "2016",
        title: "AlphaGo Defeats World Champion",
        event:
          "DeepMind's AlphaGo used supervised learning (from human games) + reinforcement learning (self-play) to defeat Lee Sedol at Go. Proved RL could master incredibly complex tasks.",
      },
      {
        year: "2020-Present",
        title: "RLHF: Training ChatGPT",
        event:
          "Reinforcement Learning from Human Feedback (RLHF) revolutionized AI assistants. ChatGPT, Claude, and others use supervised learning (on text) + RL (from human ratings) to become helpful and safe.",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {timeline.map((item, idx) => (
          <Card
            key={idx}
            title={
              <span style={{ color: "#0071e3" }}>
                {item.year} - {item.title}
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>{item.event}</Paragraph>
          </Card>
        ))}
      </Space>
    );
  };

  const tabItems = [
    {
      key: "supervised",
      label: "Supervised",
      children: renderSupervised(),
    },
    {
      key: "unsupervised",
      label: "Unsupervised",
      children: renderUnsupervised(),
    },
    {
      key: "reinforcement",
      label: "Reinforcement",
      children: renderReinforcement(),
    },
    {
      key: "overview",
      label: "Comparision",
      children: renderOverview(),
    },
    {
      key: "history",
      label: "History",
      children: renderHistory(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Three Ways AI Learns</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Supervised, Unsupervised, and Reinforcement Learning - the foundations
          of modern AI
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="supervised" items={tabItems} size="large" />
    </div>
  );
};

export default LearningTechniques;
